import { useEffect, useRef } from 'react';

/**
 * useModal — hook di accessibilità condiviso per tutti i modali dell'app.
 *
 * Audit 2026-09-20, punto #11: verificato su tutti e nove i modali esistenti
 * (BookingModal, CarFormModal, ProfileModal, ReviewModal, WelcomeModal,
 * CarDetailModal, AssociaClienteModal, CompletionModal,
 * TermsAndConditionsPopup) — nessuno chiudeva con Escape, nessuno aveva
 * role="dialog"/aria-modal, nessuno intrappolava il focus, nessuno lo
 * restituiva al trigger alla chiusura, e solo uno bloccava lo scroll della
 * pagina sottostante. Effetto pratico: Esc non faceva nulla, scrollare dentro
 * il modale fino in fondo faceva scorrere anche la pagina dietro, e Tab
 * portava a navigare i link della pagina sottostante col modale ancora aperto.
 *
 * Rilevante anche perché l'European Accessibility Act si applica dal giugno
 * 2025 ai servizi digitali venduti a consumatori nell'UE — e questo SaaS
 * genera siti di e-commerce (prenotazione auto) per i suoi clienti.
 *
 * USO:
 *   const modalRef = useModal(onClose);
 *   <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="titolo-id">
 *
 * onClose può essere `undefined`/omesso per un modale senza pulsante di
 * chiusura esplicito: in quel caso Escape non fa nulla, ma focus trap,
 * blocco scroll e restituzione del focus restano comunque attivi.
 *
 * Parametro `active` (default true): alcuni modali (es. WelcomeModal,
 * CompletionModal) restano SEMPRE montati e decidono internamente se
 * mostrarsi o restituire null, invece di essere montati/smontati dal
 * genitore solo quando visibili. Per questi, passa `active` legato allo
 * stesso stato che decide la visibilità: l'hook resta comunque chiamato ad
 * ogni render (regola di React sugli hook), ma blocco scroll/focus trap/
 * Escape si attivano/disattivano insieme alla reale visibilità invece che al
 * mount/unmount del componente.
 *
 * MODALI ANNIDATI (es. TermsAndConditionsPopup dentro BookingModal): ogni
 * istanza registra il proprio handler Escape su `document`, quindi senza
 * coordinamento un solo Esc chiuderebbe ENTRAMBI i livelli in una volta.
 * MODAL_STACK tiene traccia di quali istanze sono attualmente montate, in
 * ordine di apertura; l'handler di ciascuna verifica di essere quella più in
 * cima (l'ultima aperta) prima di reagire a Escape — quella sotto aspetta il
 * proprio turno. Il focus trap non ha lo stesso problema (è scoped al proprio
 * ref) e non usa lo stack.
 */
const MODAL_STACK = [];

export function useModal(onClose, active = true) {
  const ref = useRef(null);
  // Tiene sempre l'ultima versione di onClose, aggiornata ad ogni render.
  // L'effetto sotto ha deps [active] (si riattiva solo quando il modale
  // diventa visibile/invisibile, non ad ogni render) — senza questo ref,
  // l'handler di Escape chiuderebbe sempre sulla versione di onClose
  // catturata al mount, anche se il genitore ne passa una diversa più tardi
  // (es. per riflettere uno stato interno cambiato, come "saving").
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;

    const token = {};
    MODAL_STACK.push(token);

    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Solo l'istanza più in cima allo stack reagisce — vedi commento
        // "MODALI ANNIDATI" sopra.
        if (MODAL_STACK[MODAL_STACK.length - 1] !== token) return;
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = ref.current.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    // Sposta il focus dentro il modale all'apertura, sul primo elemento
    // interattivo (o sul contenitore stesso se non ce ne sono).
    const firstFocusable = ref.current?.querySelector(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    } else if (ref.current) {
      ref.current.setAttribute('tabindex', '-1');
      ref.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      const idx = MODAL_STACK.indexOf(token);
      if (idx !== -1) MODAL_STACK.splice(idx, 1);
      document.body.style.overflow = prevOverflow;
      // Restituisce il focus al trigger che aveva aperto il modale — un
      // utente da tastiera/screen reader altrimenti si ritrova il focus
      // "perso" in cima alla pagina alla chiusura.
      if (prevFocus && typeof prevFocus.focus === 'function') {
        prevFocus.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return ref;
}
